import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Menu, MessageCircle, User, Store, Sun, Moon, LayoutDashboard, LogOut, Package, Folder } from 'lucide-react';
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle, SheetDescription } from '../ui/sheet';
import { Button } from '../ui/button';
import MegaMenu from '../MegaMenu';

const HeaderMobileDrawer = ({
  open,
  onOpenChange,
  session,
  onLogout,
  onPostRequirement,
  theme,
  toggleTheme,
  lang,
  setLang,
  t,
}) => {
  const navigate = useNavigate();
  const close = () => onOpenChange(false);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetTrigger asChild>
        <button className="lg:hidden p-2 text-gray-600 dark:text-gray-300 order-2 ml-auto" data-testid="hamburger-btn"><Menu className="w-6 h-6" /></button>
      </SheetTrigger>
      <SheetContent side="left" className="w-[88%] max-w-sm p-0 overflow-y-auto" data-testid="mobile-drawer">
        <SheetHeader>
          <SheetTitle className="sr-only">Menu</SheetTitle>
          <SheetDescription className="sr-only">Navigation menu</SheetDescription>
        </SheetHeader>
        <div className="p-4 border-b border-gray-200 dark:border-gray-800 flex items-center justify-between pr-12">
          <h2 className="text-lg font-bold text-[#2c54b2] dark:text-orange-400">Menu</h2>
        </div>

        <div className="px-3 pt-3 pb-2 border-b border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-[#0b1220]">
          <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2 px-1">Preferences</div>
          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={toggleTheme}
              className="flex items-center justify-center gap-2 px-3 py-2.5 rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-[#111827] text-sm font-medium text-gray-800 dark:text-gray-100 hover:border-[#ee5e23]"
              data-testid="mobile-theme-toggle"
            >
              {theme === 'dark' ? <Sun className="w-4 h-4 text-amber-500" /> : <Moon className="w-4 h-4 text-[#2c54b2]" />}
              <span>{theme === 'dark' ? 'Light mode' : 'Dark mode'}</span>
            </button>
            <div className="flex items-center bg-white dark:bg-[#111827] border border-gray-300 dark:border-gray-700 rounded overflow-hidden">
              <button onClick={() => setLang('en')} className={`flex-1 px-2 py-2.5 text-xs font-medium ${lang === 'en' ? 'bg-[#ee5e23] text-white' : 'text-gray-700 dark:text-gray-200'}`} data-testid="mobile-lang-en">EN</button>
              <button onClick={() => setLang('te')} className={`flex-1 px-2 py-2.5 text-xs font-medium ${lang === 'te' ? 'bg-[#ee5e23] text-white' : 'text-gray-700 dark:text-gray-200'}`} data-testid="mobile-lang-te">తెలుగు</button>
            </div>
          </div>
        </div>

        {session ? (
          <div className="p-4 border-b border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-[#0b1220]" data-testid="mobile-account-section">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-[#ee5e23] text-white flex items-center justify-center font-bold">
                {(session.name || 'U').charAt(0).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-gray-900 dark:text-gray-100 truncate">{session.name}</div>
                <div className="text-xs text-gray-500 dark:text-gray-400">Logged in as <b>{session.role}</b></div>
              </div>
            </div>
            <div className="flex gap-2">
              {session.role === 'vendor' ? (
                <Button onClick={() => { close(); navigate('/vendor/dashboard'); }} className="flex-1 bg-[#2c54b2] hover:bg-[#1f3f87] text-white text-sm h-9" data-testid="mobile-vendor-dashboard">
                  <LayoutDashboard className="w-4 h-4 mr-1" /> Dashboard
                </Button>
              ) : (
                <Button onClick={() => { close(); navigate('/account'); }} className="flex-1 bg-[#2c54b2] hover:bg-[#1f3f87] text-white text-sm h-9" data-testid="mobile-my-account">
                  <User className="w-4 h-4 mr-1" /> My Account
                </Button>
              )}
              <Button onClick={() => { onLogout(); close(); }} variant="outline" className="text-red-600 border-red-200 hover:bg-red-50 text-sm h-9" data-testid="mobile-logout">
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        ) : (
          <div className="p-4 border-b border-gray-200 dark:border-gray-800 grid grid-cols-2 gap-2" data-testid="mobile-auth-buttons">
            <Button onClick={() => { close(); navigate('/login'); }} className="bg-[#ee5e23] hover:bg-[#d24e15] text-white h-10" data-testid="mobile-signin-btn">
              <User className="w-4 h-4 mr-1" /> Sign In
            </Button>
            <Button onClick={() => { close(); navigate('/register'); }} variant="outline" className="border-[#2c54b2] text-[#2c54b2] dark:text-orange-400 dark:border-orange-400 h-10" data-testid="mobile-register-btn">
              Register
            </Button>
          </div>
        )}

        <div className="p-3 border-b border-gray-200 dark:border-gray-800" data-testid="mobile-quick-links">
          <Button onClick={() => { close(); onPostRequirement(); }} className="w-full bg-[#ee5e23] hover:bg-[#d24e15] text-white h-10 mb-2" data-testid="mobile-post-requirement">
            {t('cta.post_requirement')}
          </Button>
          <div className="space-y-1 text-sm">
            {session?.role !== 'vendor' && (
              <Link to="/vendor/register" onClick={close} className="flex items-center gap-2 px-2 py-2 rounded hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200" data-testid="mobile-become-vendor">
                <Store className="w-4 h-4 text-[#2c54b2] dark:text-orange-400" /> {t('cta.become_vendor')}
              </Link>
            )}
            {!session && (
              <Link to="/login?as=vendor" onClick={close} className="flex items-center gap-2 px-2 py-2 rounded hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200" data-testid="mobile-vendor-login">
                <LayoutDashboard className="w-4 h-4 text-[#2c54b2] dark:text-orange-400" /> Vendor Login
              </Link>
            )}
            {!session && (
              <Link to="/account" onClick={close} className="flex items-center gap-2 px-2 py-2 rounded hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200" data-testid="mobile-my-account-link">
                <User className="w-4 h-4 text-[#2c54b2] dark:text-orange-400" /> {t('nav.account')}
              </Link>
            )}
            <Link to="/directory" onClick={close} className="flex items-center gap-2 px-2 py-2 rounded hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200" data-testid="mobile-directory">
              <Folder className="w-4 h-4 text-[#2c54b2] dark:text-orange-400" /> Business Directory
            </Link>
            <Link to="/compare" onClick={close} className="flex items-center gap-2 px-2 py-2 rounded hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200" data-testid="mobile-compare">
              <Package className="w-4 h-4 text-[#2c54b2] dark:text-orange-400" /> {t('nav.compare')}
            </Link>
            <Link to="/p/help" onClick={close} className="flex items-center gap-2 px-2 py-2 rounded hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200" data-testid="mobile-help">
              <MessageCircle className="w-4 h-4 text-[#2c54b2] dark:text-orange-400" /> {t('nav.help')}
            </Link>
          </div>
        </div>

        <div className="px-4 pt-3 pb-1">
          <h3 className="text-xs font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">{t('nav.categories')}</h3>
        </div>
        <div onClick={(e) => { if (e.target.closest('a')) close(); }}>
          <MegaMenu variant="drawer" />
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default HeaderMobileDrawer;
